import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { REPORT_COLORS } from 'pages/reporting/reports/rug/constants/rugReportStyles'
import type { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import React from 'react'
import { GeckoMeasurement } from 'types'

interface SectionStats {
  sectionName: string
  sectionRange?: string
  sectionRangeLabel?: 'Elevation' | 'Tubes'
  minThickness: GeckoMeasurement
  averageThickness: GeckoMeasurement
  wallLossPercent?: GeckoMeasurement
  nominalThickness?: GeckoMeasurement
  tMin?: GeckoMeasurement
}

const createTableStyles = (
  includesTMin: boolean,
  includesSectionRange: boolean
) =>
  StyleSheet.create({
    tableRow: {
      flexDirection: 'row',
      borderBottom: '1px solid black',
      minHeight: 15,
    },
    tableCell: {
      borderRight: '1px solid black',
      padding: 4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    tableCellNarrow: {
      width: `${100 / (5 + (includesTMin ? 1 : 0) + (includesSectionRange ? 1 : 0))}%`,
    },
    headerText: {
      fontSize: 9,
      fontWeight: 'bold',
      color: REPORT_COLORS.HEADER_TEXT,
    },
    cellText: {
      fontSize: 8,
    },
  })

interface SectionStatsRowProps {
  sectionStats: SectionStats
  includesTMin: boolean
  includesSectionRange: boolean
  formatMeasurement: ReturnType<typeof useRugReportOps>['formatMeasurement']
  isLastRow: boolean
  tableStyles: ReturnType<typeof createTableStyles>
}

const SectionStatsRow: React.FC<SectionStatsRowProps> = ({
  sectionStats,
  includesTMin,
  includesSectionRange,
  tableStyles,
  formatMeasurement,
  isLastRow,
}) => {
  return (
    <View
      wrap={false}
      style={[tableStyles.tableRow, { marginBottom: isLastRow ? 24 : 0 }]}>
      <View
        style={[
          tableStyles.tableCell,
          tableStyles.tableCellNarrow,
          { borderLeft: '1px solid black' },
        ]}>
        <Text style={tableStyles.cellText}>{sectionStats.sectionName}</Text>
      </View>
      {includesSectionRange && (
        <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
          <Text style={tableStyles.cellText}>
            {sectionStats.sectionRange ?? 'N/A'}
          </Text>
        </View>
      )}
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {sectionStats.nominalThickness
            ? formatMeasurement({
                measurement: sectionStats.nominalThickness,
                variant: 'narrow',
                minimumFractionDigits: 3,
              })
            : 'N/A'}
        </Text>
      </View>
      {includesTMin && (
        <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
          <Text style={tableStyles.cellText}>
            {sectionStats.tMin
              ? formatMeasurement({
                  measurement: sectionStats.tMin,
                  variant: 'narrow',
                  minimumFractionDigits: 3,
                })
              : 'N/A'}
          </Text>
        </View>
      )}
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {formatMeasurement({
            measurement: sectionStats.averageThickness,
            variant: 'narrow',
            minimumFractionDigits: 3,
          })}
        </Text>
      </View>
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {formatMeasurement({
            measurement: sectionStats.minThickness,
            variant: 'narrow',
            minimumFractionDigits: 3,
          })}
        </Text>
      </View>
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {sectionStats.wallLossPercent
            ? formatMeasurement({
                measurement: sectionStats.wallLossPercent,
                variant: 'narrow',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : 'N/A'}
        </Text>
      </View>
    </View>
  )
}

interface SectionStatsTableProps {
  sectionStats: SectionStats[]
  includeTMin: boolean
  componentName?: string
  formatMeasurement: ReturnType<typeof useRugReportOps>['formatMeasurement']
}

export const SectionStatsTable: React.FC<SectionStatsTableProps> = ({
  sectionStats,
  includeTMin,
  componentName,
  formatMeasurement,
}) => {
  const shouldIncludeTMin =
    includeTMin && sectionStats.some((stats) => stats.tMin !== undefined)
  const shouldIncludeSectionRange = sectionStats.some(
    (stats) => stats.sectionRange !== undefined
  )
  const sectionRangeLabel =
    sectionStats.find((stats) => stats.sectionRangeLabel)?.sectionRangeLabel ??
    'Elevation'
  const tableStyles = createTableStyles(
    shouldIncludeTMin,
    shouldIncludeSectionRange
  )

  const renderRow = (stats: SectionStats) => (
    <SectionStatsRow
      key={stats.sectionName}
      sectionStats={stats}
      formatMeasurement={formatMeasurement}
      includesTMin={shouldIncludeTMin}
      includesSectionRange={shouldIncludeSectionRange}
      tableStyles={tableStyles}
      isLastRow={stats === sectionStats.at(-1)}
    />
  )

  return (
    <>
      <View wrap={false}>
        {componentName && (
          <Text
            style={{
              fontSize: 10,
              fontWeight: 'bold',
              marginBottom: 6,
              color: REPORT_COLORS.HEADER_TEXT,
            }}>
            {componentName}
          </Text>
        )}
        <View style={{ borderTop: '1px solid black' }}>
          <View
            style={[tableStyles.tableRow, { borderBottom: '2px solid black' }]}>
            <View
              style={[
                tableStyles.tableCell,
                tableStyles.tableCellNarrow,
                { borderLeft: '1px solid black' },
              ]}>
              <Text style={tableStyles.headerText}>Section</Text>
            </View>
            {shouldIncludeSectionRange && (
              <View
                style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
                <Text style={tableStyles.headerText}>{sectionRangeLabel}</Text>
              </View>
            )}
            <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
              <Text style={tableStyles.headerText}>Nominal</Text>
            </View>
            {shouldIncludeTMin && (
              <View
                style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
                <Text style={tableStyles.headerText}>T-Min</Text>
              </View>
            )}
            <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
              <Text style={tableStyles.headerText}>Average</Text>
            </View>
            <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
              <Text style={tableStyles.headerText}>Minimum</Text>
            </View>
            <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
              <Text style={tableStyles.headerText}>Max Wall Loss</Text>
            </View>
          </View>
          {sectionStats[0] && renderRow(sectionStats[0])}
        </View>
      </View>
      {sectionStats.slice(1).map(renderRow)}
    </>
  )
}
